// Browser error capture (D-043 browser-ui) posting to the site's own endpoint;
// no third-party service. The listeners are installed when this module is
// evaluated, before React hydrates, so hydration failures are seen too. At most
// MAX_REPORTS distinct reports leave one page load; nothing the visitor typed
// is sent, only the error, the script address and the page path.

import { CLIENT_REPORT_ENDPOINT, RELEASE_META, REQUEST_ID_META, type BrowserErrorReport } from "../../contracts";

const MAX_REPORTS = 5;

let sent = 0;
const seen = new Set<string>();

function meta(name: string): string | undefined {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") ?? undefined;
}

function cut(value: string | undefined, max: number): string | undefined {
  return typeof value === "string" ? value.slice(0, max) : undefined;
}

/** Sends one report; never throws and never retries. */
export function reportBrowserError(report: BrowserErrorReport): void {
  try {
    if (typeof window === "undefined" || sent >= MAX_REPORTS) return;
    const key = [report.kind, report.type, report.message, report.source, report.line, report.digest].join("|");
    if (seen.has(key)) return;
    seen.add(key);
    sent += 1;
    const body = JSON.stringify({
      ...report,
      message: cut(report.message, 300),
      stack: cut(report.stack, 4000),
      page: window.location.pathname,
      release: meta(RELEASE_META),
      requestId: meta(REQUEST_ID_META),
    });
    void fetch(CLIENT_REPORT_ENDPOINT, { method: "POST", keepalive: true, credentials: "same-origin", headers: { "Content-Type": "application/json" }, body }).catch(() => undefined);
  } catch {
    // Reporting must never raise a second error.
  }
}

function describe(value: unknown): Pick<BrowserErrorReport, "type" | "message" | "stack"> {
  if (value instanceof Error) return { type: value.name, message: value.message, stack: value.stack };
  if (typeof value === "string") return { type: "string", message: value };
  return { type: value === null ? "null" : typeof value };
}

const INSTALLED = "__ironErrorCapture";

function install(): void {
  const target = window as typeof window & { [INSTALLED]?: boolean };
  if (target[INSTALLED]) return;
  target[INSTALLED] = true;
  // Without capture, only script errors reach window; failed images and media do not.
  window.addEventListener("error", (event) => {
    if (!(event instanceof ErrorEvent)) return;
    const described = describe(event.error);
    reportBrowserError({ kind: "error", ...described, message: event.message || described.message, source: event.filename, line: event.lineno, column: event.colno });
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportBrowserError({ kind: "unhandledrejection", ...describe(event.reason) });
  });
}

if (typeof window !== "undefined") install();
